import LayoutSidebar from "@/layouts/LayoutSidebar/LayoutSidebar.vue";
import DetailIntroText from "@/components/DetailIntroText/DetailIntroText.vue";
import WordService from "@/services/WordService";
import { adSubdomain, isSmallDevice, isMediumDevice } from "@/common/functions";
import { root } from "@/common/consts";
import { mapGetters } from "vuex";
import Advertising from "@/components/Advertising/Advertising.vue";
import OneTrustService from "@/services/OneTrustService";

const TaboolaAd = () =>
    import(/* webpackChunkName: "TaboolaAd" */ "@/components/TaboolaAd/TaboolaAd.vue");

export default {
    components: {
        Advertising,
        Breadcrumb: () =>
            import(/* webpackChunkName: "breadcrumb" */ "@/components/Breadcrumb/Breadcrumb.vue"),
        HeadWord: () =>
            import(/* webpackChunkName: "headWord" */ "@/components/HeadWord/HeadWord.vue"),
        LayoutSidebar,
        MentionedIn: () =>
            import(
                /* webpackChunkName: "mentionedIn" */ "@/components/MentionedIn/MentionedIn.vue"
            ),
        NearbyWords: () =>
            import(
                /* webpackChunkName: "nearbyWords" */ "@/components/NearbyWords/NearbyWords.vue"
            ),
        RelatedArticles: () =>
            import(
                /* webpackChunkName: "relatedArticles" */ "@/components/RelatedArticles/RelatedArticles.vue"
            ),
        DetailIntroText,
        SentenceItem: () =>
            import(
                /* webpackChunkName: "sentenceItem" */ "@/components/SentenceItem/SentenceItem.vue"
            ),
        TaboolaAd
    },
    props: {
        slug: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            status: {
                err: false,
                errmsg: ""
            },
            layout: LayoutSidebar,
            word: "",
            audio: "",
            sentences: [],
            total: "",
            showMobileAdsInRows: [2, 5, 8, 11, 14, 17, 20, 23, 26, 29],
            mobileReuseAds: [
                "Mobile_Sentence_Detail_InContent_Top",
                "Mobile_Sentence_Detail_InContent_Middle"
            ],
            showInContentAdsInRow: [4, 9, 14, 19, 24, 29, 34, 39, 44, 49],
            inContentReuseAds: ["Desktop_Sentence_Detail_Native"],
            subdomain: adSubdomain(this.$route.meta.subdomain),
            canonical: ``
        };
    },
    methods: {
        loadSentences(limit = 500) {
            WordService.getSentences(this.$route.params.slug, limit)
                .then(response => {
                    const word = response.data.data;
                    this.word = word.headword;
                    this.audio = word.audio;
                    this.sentences = word.sentences;

                    const wordMeta = response.data.meta;
                    this.total = wordMeta.total;

                    this.formatTotal();
                })
                // eslint-disable-next-line no-unused-vars
                .catch(err => {
                    if (typeof window.__PRERENDER_INJECTED !== "undefined") {
                        window.document.dispatchEvent(
                            new CustomEvent("spa-render-404", {
                                detail: { component: this }
                            })
                        );
                        return;
                    }

                    // Eject the user back to home if we don't have results for this word.
                    if (this.subdomain === "Sentence") {
                        window.location.assign(`${root.WWW}/`);
                    } else if (this.subdomain === "Es") {
                        window.location.assign(`${root.ES}/`);
                    }
                });
        },
        getMobileAd(rowIndex) {
            const index = this.showMobileAdsInRows.indexOf(rowIndex);
            return index >= 0 ? this.mobileReuseAds[index] : "";
        },
        displayMobileAdAtIndex(rowIndex) {
            return this.showMobileAdsInRows.includes(rowIndex) && isSmallDevice();
        },
        getInContentAd(rowIndex) {
            const index = this.showInContentAdsInRow.indexOf(rowIndex);
            return index >= 0 ? this.inContentReuseAds[index] : "";
        },
        displayInContentAdAtIndex(rowIndex) {
            /** Consider the case that there are less than five sentences */
            this.sentences.length < 5
                ? (this.showInContentAdsInRow[0] = 0)
                : (this.showInContentAdsInRow[0] = 4);

            return this.showInContentAdsInRow.includes(rowIndex) && !isMediumDevice();
        },
        formatTotal() {
            const formattedResults = this.total.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
            this.total = formattedResults;
        }
    },
    computed: {
        ...mapGetters(["currentLanguage"]),
        ...mapGetters("Login", ["user"])
    },
    beforeRouteUpdate(to, from, next) {
        next();
        this.loadSentences();
    },
    metaInfo() {
        const word = this.word || this.$route.params.slug; // Use the slug until the proper word spelling is available from the API call.
        const total = this.total;
        return {
            title: this.$t(`sentenceDetailPageMetaTitle`, { word: `${word}` }),
            meta: [
                {
                    name: "description",
                    content: this.$t(`sentenceDetailMetaDescription`, {
                        word: `${word}`,
                        sentenceResultsNumber: `${total}`
                    })
                }
            ],
            link: [
                {
                    rel: "canonical",
                    href: this.canonical + `${word}`
                }
            ]
        };
    },
    created() {
        if (typeof window.__PRERENDER_INJECTED === "undefined") {
            // eslint-disable-next-line no-undef
            hj("trigger", "test");
        }

        switch (this.currentLanguage) {
            case "en":
                this.mobileReuseAds = [
                    "Mobile_Sentence_Detail_InContent_Top",
                    "Mobile_Sentence_Detail_InContent_Middle"
                ];
                this.canonical = `https://sentence.yourdictionary.com/`;
                break;
            case "es":
                this.mobileReuseAds = [
                    "Mobile_ES_Sentence_Detail_InContent_Top",
                    "Mobile_ES_Sentence_Detail_InContent_Middle"
                ];
                this.canonical = `https://es.yourdictionary.com/frases/`;
                break;
            default:
                this.mobileReuseAds = [
                    "Mobile_Sentence_Detail_InContent_Top",
                    "Mobile_Sentence_Detail_InContent_Middle"
                ];
                this.canonical = `https://sentence.yourdictionary.com/`;
        }
    },
    mounted() {
        this.loadSentences();
        if (typeof window.__PRERENDER_INJECTED === "undefined") {
            // Run only on client side
            OneTrustService.setListeners(this);
            permutive.addon("web", {
                page: {
                    data: {
                        slug: this.$route.path,
                        title: this.$metaInfo.title,
                        type: "search_results",
                        section: "Sentence (V2)",
                        subsection: "Detail"
                    }
                }
            });
        }
    }
};



// WEBPACK FOOTER //
// ./src/pages/Sentences/Detail/SentenceDetail.js